import React from 'react';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FiInfo } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { AccessRightEnum } from '../../../../../../enum/AccessRightEnum';
import { IInfluencerReport } from '../../../../../../model/measure/IInfluencerReport';
import { IReportedPlatforms } from '../../../../../../model/measure/IReportedPlatforms';
import { User } from '../../../../../../model/user/User';
import { CustomImageHandler } from '../../../../../../shared/components/CustomImageHandler';
import EmptyState from '../../../../../../shared/components/placeholder/EmptyState';
import { getSocialIcons, truncateString } from '../../../../../../shared/functions/Functions';
import { IAllStates } from '../../../../../../store/rootReducer';
import { priceSplitter } from '../../../../../../utils/helpers/priceSplitter';
import CardLayout from '../../../../../../shared/components/card/CardLayout';
import TooltipOverlay from '../../../../../../shared/components/tooltip/Tooltip';
import { DetailedPerPlatformTableSkeletonBody } from './DetailedPerPlatformTableSkeleton';

interface IProps {
    influencerData: IInfluencerReport[];
    isLoading?: boolean;
}

export const DetailedPerPlatformTable = ({ influencerData, isLoading = false }: IProps) => {
    const { t } = useTranslation();
    const { accessRights } = useSelector((state: IAllStates) => state.auth.user);
    const hideCosts = accessRights.includes(AccessRightEnum.MEASURE_HIDE_COSTS);
    console.log(isLoading);

    const getSumValues = (
        platforms: IInfluencerReport[] | undefined,
        key: keyof IReportedPlatforms,
        calcAvg = false,
        getCalcNum = false
    ) => {
        let sum = 0;
        platforms &&
            platforms?.forEach(platform => {
                if (platform?.reportedPlatforms?.length) {
                    sum += platform?.reportedPlatforms?.reduce(
                        (prev, curr) => (Number(curr[key]) ? prev + Number(curr[key]) : prev),
                        0
                    );
                }
            });
        platforms &&
            platforms?.forEach(platform => {
                if (
                    platform?.additionalReportedItems?.length &&
                    !['totalExecutedPrice', 'reachCpm', 'nbPublishedPosts']?.includes(key)
                ) {
                    // @ts-ignore
                    sum += platform?.additionalReportedItems?.reduce(
                        // @ts-ignore
                        (prev, curr) => (Number(curr[key]) ? prev + Number(curr[key]) : prev),
                        0
                    );
                }
            });
        return !calcAvg && !getCalcNum
            ? +sum?.toFixed(2)
            : getCalcNum
            ? sum
            : (sum / (platforms ?? [])?.length)?.toFixed(2);
    };

    return (
        <CardLayout>
            {isLoading || !!influencerData?.length ? (
                <Table responsive className="table-vertical-center mb-0">
                    <thead className="border-bottom">
                        <tr>
                            <th>{t('general.influencer')}</th>
                            <th>{t('brand.campaign.measure.placement')}</th>
                            <th className="text-center">{t('brand.campaign.measure.reported')}</th>
                            <th className="text-center ">
                                <div className="d-flex align-items-center justify-content-center">
                                    {t('brand.campaign.measure.impressions')}
                                    <TooltipOverlay
                                        className="ms-1"
                                        content={t(`brand.campaign.measure.impressionsTooltip`)}
                                    >
                                        <FiInfo size={14} />
                                    </TooltipOverlay>
                                </div>
                            </th>
                            <th className="text-center ">
                                <div className="d-flex align-items-center justify-content-center">
                                    {t('brand.campaign.measure.reach')}
                                    <TooltipOverlay className="ms-1" content={t(`brand.campaign.measure.reachTooltip`)}>
                                        <FiInfo size={14} />
                                    </TooltipOverlay>
                                </div>
                            </th>
                            <th className="text-center ">
                                <div className="d-flex align-items-center justify-content-center">
                                    {t('brand.campaign.measure.engagements')}
                                    <TooltipOverlay
                                        className="ms-1"
                                        content={t(`brand.campaign.measure.engagementsTooltip`)}
                                    >
                                        <FiInfo size={14} />
                                    </TooltipOverlay>
                                </div>
                            </th>
                            <th className="text-center ">
                                <div className="d-flex align-items-center justify-content-center">
                                    {t('influencer.reporting.totalLinkClicks')}
                                    <TooltipOverlay
                                        className="ms-1"
                                        content={t(`brand.campaign.measure.clicksTooltip`)}
                                    >
                                        <FiInfo size={14} />
                                    </TooltipOverlay>
                                </div>
                            </th>
                            <th className="text-center ">
                                <div className="d-flex align-items-center justify-content-center">
                                    {t('brand.campaign.measure.engagementRate')}
                                    <TooltipOverlay
                                        className="ms-1"
                                        content={t(`brand.campaign.measure.engagementRateTooltip`)}
                                    >
                                        <FiInfo size={14} />
                                    </TooltipOverlay>
                                </div>
                            </th>
                            {!hideCosts && (
                                <>
                                    <th className="text-center ">
                                        <div className="d-flex align-items-center justify-content-center">
                                            {t('brand.campaign.measure.reachCpm')}
                                            <TooltipOverlay
                                                className="ms-1"
                                                content={t(`brand.campaign.measure.newCpmTooltip`)}
                                            >
                                                <FiInfo size={14} />
                                            </TooltipOverlay>
                                        </div>
                                    </th>
                                    <th className="text-center ">
                                        <div className="d-flex align-items-center justify-content-center">
                                            {t('brand.campaign.measure.costs')}
                                            <TooltipOverlay
                                                placement="top"
                                                content={t(`brand.campaign.measure.costsTooltip`)}
                                            >
                                                <FiInfo size={14} />
                                            </TooltipOverlay>
                                        </div>
                                    </th>
                                </>
                            )}
                        </tr>
                    </thead>
                    {isLoading ? (
                        <DetailedPerPlatformTableSkeletonBody hideCosts={hideCosts} />
                    ) : (
                        <tbody>
                            {influencerData?.map((platform, index) => {
                                const { isAddonsOnly, isLongTerm } = platform;
                                const isLastLoop = index === influencerData?.length - 1;
                                const name = platform?.user?.displayName;
                                const truncatedName = truncateString(name, 25);
                                const isTruncated = name.length > 25;
                                return (
                                    <React.Fragment key={platform?.id}>
                                        <tr className={'bg-muted border-bottom border-top'}>
                                            <td className="px-0 py-2" colSpan={10}>
                                                <div className="d-flex align-items-center pl-2">
                                                    <CustomImageHandler
                                                        thumbnailPath={
                                                            platform?.user?.profilePhoto?.thumbnail as string
                                                        }
                                                        photoPath={platform?.user?.profilePhoto?.path as string}
                                                        classes="campaign-icon rounded-circle"
                                                        altTag="User Profile"
                                                        name={name}
                                                    />
                                                    <TooltipOverlay content={isTruncated ? name : ''}>
                                                        <h6 className="mb-0 ml-2 mr-2">{truncatedName || '-'}</h6>
                                                    </TooltipOverlay>
                                                    {isLongTerm && (
                                                        <div className={'custom-badge long-term ml-1'}>
                                                            {t(`brand.campaign.briefing.overview_card.long_term`)}
                                                        </div>
                                                    )}
                                                    {isAddonsOnly && (
                                                        <div className={'custom-badge additional ml-1'}>
                                                            {t('influencer.offer_management.modal.additional')}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        {platform?.reportedPlatforms?.map((reportedPlatform, index) => {
                                            return (
                                                <React.Fragment key={reportedPlatform?.campaign + index}>
                                                    <tr
                                                        key={reportedPlatform?.campaign + index}
                                                        className={'border-bottom'}
                                                    >
                                                        <td />
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                {getSocialIcons({
                                                                    iconPath: reportedPlatform?.socialPlatform.path,
                                                                })}
                                                                <div className="ml-2 mb-0 font-weight-normal">
                                                                    {t(
                                                                        `general.platforms.${reportedPlatform?.socialPlatform?.name}`
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-0 py-2 text-center">
                                                            {reportedPlatform?.nbPublishedPosts ?? 'n.a.'} /{' '}
                                                            {reportedPlatform?.nbOfferedPosts ?? 'n.a.'}
                                                        </td>
                                                        <td className="px-0 py-2 text-center">
                                                            {!!+reportedPlatform?.impressions
                                                                ? priceSplitter(Number(reportedPlatform.impressions))
                                                                : 'n.a.'}
                                                        </td>
                                                        <td className={`px-0 py-2 text-center`}>
                                                            {!!+reportedPlatform.reach
                                                                ? priceSplitter(+reportedPlatform.reach)
                                                                : 'n.a.'}
                                                        </td>
                                                        <td className="px-0 py-2 text-center">
                                                            {!!+(reportedPlatform?.engagements ?? 0)
                                                                ? priceSplitter(+reportedPlatform.engagements)
                                                                : 'n.a.'}
                                                        </td>
                                                        <td className="px-0 py-2 text-center">
                                                            {!!+(reportedPlatform?.totalLinkClicks ?? 0)
                                                                ? priceSplitter(+reportedPlatform.totalLinkClicks)
                                                                : 'n.a.'}
                                                        </td>

                                                        <td className="px-0 py-2 text-center">
                                                            {!!+(reportedPlatform?.engagementRate ?? 0)
                                                                ? priceSplitter(+reportedPlatform.engagementRate)
                                                                : 'n.a.'}{' '}
                                                            %
                                                        </td>
                                                        {!hideCosts && (
                                                            <>
                                                                <td className="px-0 py-2 text-center euro-symbol">
                                                                    {!!+(reportedPlatform?.reachCpm ?? 0)
                                                                        ? priceSplitter(
                                                                              +(
                                                                                  reportedPlatform.reachCpm || 0
                                                                              )?.toFixed(2)
                                                                          )
                                                                        : 'n.a.'}
                                                                </td>
                                                                <td className="px-0 py-2 text-center euro-symbol">
                                                                    {!!+(reportedPlatform?.totalExecutedPrice ?? 0)
                                                                        ? priceSplitter(
                                                                              +(
                                                                                  reportedPlatform.totalExecutedPrice ||
                                                                                  0
                                                                              )?.toFixed(2)
                                                                          )
                                                                        : 'n.a.'}
                                                                </td>
                                                            </>
                                                        )}
                                                    </tr>
                                                </React.Fragment>
                                            );
                                        })}
                                        {(platform?.additionalReportedItems as unknown as IReportedPlatforms[])?.map(
                                            (reportedPlatform, index) => {
                                                return (
                                                    <React.Fragment key={index + reportedPlatform.platform}>
                                                        <tr key={index} className={'border-bottom'}>
                                                            <td>
                                                                <h6 className="mb-0">
                                                                    {t('brand.campaign.measure.additional')}
                                                                </h6>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    {getSocialIcons({
                                                                        iconPath:
                                                                            reportedPlatform?.socialPlatform?.path,
                                                                    })}
                                                                    <div className="ml-2 mb-0 font-weight-normal">
                                                                        {t(
                                                                            `general.platforms.${reportedPlatform?.socialPlatform?.name}`
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-0 py-2 text-center">n.a.</td>
                                                            <td className="px-0 py-2 text-center">
                                                                {!!+reportedPlatform?.impressions
                                                                    ? priceSplitter(
                                                                          Number(reportedPlatform.impressions)
                                                                      )
                                                                    : 'n.a.'}
                                                            </td>
                                                            <td className={`px-0 py-2 text-center`}>
                                                                {!!+reportedPlatform.reach
                                                                    ? priceSplitter(+reportedPlatform.reach)
                                                                    : 'n.a.'}
                                                            </td>
                                                            <td className="px-0 py-2 text-center">
                                                                {!!+(reportedPlatform?.engagements ?? 0)
                                                                    ? priceSplitter(+reportedPlatform.engagements)
                                                                    : 'n.a.'}
                                                            </td>
                                                            <td className="px-0 py-2 text-center">
                                                                {!!+(reportedPlatform?.totalLinkClicks ?? 0)
                                                                    ? priceSplitter(+reportedPlatform.totalLinkClicks)
                                                                    : 'n.a.'}
                                                            </td>

                                                            <td className="px-0 py-2 text-center">
                                                                {!!+(reportedPlatform?.engagementRate ?? 0)
                                                                    ? priceSplitter(+reportedPlatform.engagementRate)
                                                                    : 'n.a.'}{' '}
                                                                %
                                                            </td>
                                                            {!hideCosts && (
                                                                <>
                                                                    <td className="px-0 py-2 text-center euro-symbol">
                                                                        n.a.
                                                                    </td>
                                                                    <td className="px-0 py-2 text-center euro-symbol">
                                                                        n.a.
                                                                    </td>
                                                                </>
                                                            )}
                                                        </tr>
                                                    </React.Fragment>
                                                );
                                            }
                                        )}

                                        {isLastLoop && (
                                            <tr className={'border-top border-bottom bg-muted'}>
                                                <td className={'pl-2'}>
                                                    <span className="font-weight-bold text-uppercase">Total</span>
                                                </td>
                                                <td />
                                                <td className="text-center font-weight-bold">
                                                    {getSumValues(influencerData, 'nbPublishedPosts')}/
                                                    {getSumValues(influencerData, 'nbOfferedPosts')}
                                                </td>
                                                <td className="text-center font-weight-bold">
                                                    {!!getSumValues(influencerData, 'impressions')
                                                        ? priceSplitter(+getSumValues(influencerData, 'impressions'))
                                                        : 'n.a.'}
                                                </td>
                                                <td className="text-center font-weight-bold">
                                                    {!!+getSumValues(influencerData, 'reach')
                                                        ? priceSplitter(+getSumValues(influencerData, 'reach'))
                                                        : 'n.a.'}
                                                </td>

                                                <td className="text-center font-weight-bold">
                                                    {!!+getSumValues(influencerData, 'engagements')
                                                        ? priceSplitter(+getSumValues(influencerData, 'engagements'))
                                                        : 'n.a.'}
                                                </td>

                                                <td className="text-center font-weight-bold">
                                                    {!!+getSumValues(influencerData, 'totalLinkClicks')
                                                        ? priceSplitter(
                                                              +getSumValues(influencerData, 'totalLinkClicks')
                                                          )
                                                        : 'n.a.'}
                                                </td>

                                                <td className="text-center font-weight-bold">
                                                    {!!+getSumValues(influencerData, 'reach')
                                                        ? priceSplitter(
                                                              +(
                                                                  (Number(getSumValues(influencerData, 'engagements')) /
                                                                      Number(getSumValues(influencerData, 'reach'))) *
                                                                  100
                                                              ).toFixed(2)
                                                          )
                                                        : 'n.a.'}{' '}
                                                    %
                                                </td>
                                                {!hideCosts && (
                                                    <>
                                                        <td className="text-center font-weight-bold euro-symbol">
                                                            {!!+getSumValues(influencerData, 'reach')
                                                                ? priceSplitter(
                                                                      +(
                                                                          (Number(
                                                                              getSumValues(
                                                                                  influencerData,
                                                                                  'totalExecutedPrice',
                                                                                  false,
                                                                                  true
                                                                              )
                                                                          ) /
                                                                              Number(
                                                                                  getSumValues(
                                                                                      influencerData,
                                                                                      'reach',
                                                                                      false,
                                                                                      true
                                                                                  )
                                                                              )) *
                                                                          1000
                                                                      ).toFixed(2)
                                                                  )
                                                                : 'n.a.'}
                                                        </td>
                                                        <td className="text-center font-weight-bold euro-symbol">
                                                            {priceSplitter(
                                                                +getSumValues(influencerData, 'totalExecutedPrice')
                                                            )}
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    )}
                </Table>
            ) : (
                <EmptyState
                    addClass="col-lg-5 col-8 text-center m-auto"
                    desc={t('brand.campaign.measure.empty_detailed')}
                />
            )}
        </CardLayout>
    );
};
